<template>
  <div class="completed-hike-item">
    <div class="completed-hike-content">
      <!-- Thumbnail with play button -->
      <div class="completed-hike-thumbnail-wrapper">
        <img :src="thumbnailUrl" class="completed-hike-thumbnail" :alt="$t('completed_hikes.completed_hike_item.trail_thumbnail')" />

        <button class="play-button" @click="onShowFlyover">
          <i class="material-icons">play_circle</i>
        </button>

        <span class="completed-badge">
          <i class="material-icons">check</i>
          {{ $t('shared.completed') }}
        </span>
      </div>

      <!-- Trail info -->
      <div class="completed-hike-info">
        <!-- Badges row -->
        <div class="badges-row">
          <span :class="['difficulty-badge', difficultyClass]">
            {{ difficultyLabel }}
          </span>

          <span v-if="hike.publiclyVisible === false" class="private-badge" :title="$t('completed_hikes.completed_hike_item.private_hike')">
            <i class="material-icons">lock</i>
          </span>
        </div>

        <p class="mb-0">
          <a :href="hike.shareableUrl" class="completed-hike-name">
            <strong>{{ hike.name }}</strong>
          </a>
        </p>
        <p class="text-muted mb-0 completed-date">
          {{ formattedDate }} · {{ locationDisplay }}
        </p>
      </div>

      <!-- Stats -->
      <div class="completed-hike-stats">
        <div class="stat">
          <span class="stat-label">{{ $t('shared.distance') }}</span>
          <p>{{ formattedDistance }}</p>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('completed_hikes.completed_hike_item.est_duration') }}</span>
          <p>{{ formattedDuration }}</p>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('shared.elevation') }}</span>
          <p>{{ formattedElevation }}</p>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="completed-hike-actions">
        <button class="btn btn-icon btn-yellow" @click="onDownload">
          <span class="material-icons text-dark p-1 d-block" style="font-size: 28px;">arrow_circle_down</span>
        </button>
        <button class="btn btn-icon" @click="onAddToList">
          <i class="material-icons">bookmark</i>
        </button>
        <div class="dropdown">
          <button class="btn btn-icon dropdown-toggle" type="button" data-bs-toggle="dropdown">
            <i class="material-icons">more_vert</i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" :href="mapUrl">{{ $t('shared.view_map') }}</a></li>
            <li><button class="dropdown-item" @click="onShowFlyover">{{ $t('shared.3d_flyover') }}</button></li>
            <li><a class="dropdown-item" :href="printUrl">{{ $t('shared.print_map') }}</a></li>
            <li><button class="dropdown-item" @click="onCopyShare">{{ $t('hikes.show.share_hike') }}</button></li>
            <li><a class="dropdown-item" :href="editUrl">{{ $t('completed_hikes.completed_hike_item.edit_in_planner') }}</a></li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import moment from 'moment'
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref'
import { HikeTimeUtility, PrettyTime } from '../../../utils/helpers/HikeTime'
import { i18n } from '../../../i18n'

const props = defineProps({
  completedHike: { type: Object, required: true }
})

const emit = defineEmits(['show-flyover', 'export-gpx', 'copy-share', 'request-download', 'add-to-list'])

const hike = computed(() => props.completedHike.hike)

const mapUrl = computed(() => `${hike.value?.shareableUrl}/map`)
const printUrl = computed(() => `/print-maps?data_ids[]=${hike.value?.id}&data_types[]=Hike`)
const editUrl = computed(() => {
  const url = hike.value?.shareableUrl || ''
  return url.replace('/hikes/users/', '/user/hikes/') + '/edit'
})
// Non-owner path: open a copy of the route in the planner (matches the hike
// overview page's "Edit a Copy" → /hike-planner?from_hike_id=...).

const thumbnailUrl = computed(() => {
  if (hike.value?.staticMapImageUrl) {
    return hike.value.staticMapImageUrl
  }
  return new URL('../../../images/static_map_thumbnail.webp', import.meta.url).href
})

const formattedDate = computed(() => {
  return moment(props.completedHike.completedAt).format('LL')
})

const locationDisplay = computed(() => {
  const parts = []
  if (hike.value?.mainRegion?.name) parts.push(hike.value.mainRegion.name)
  if (hike.value?.mainCountry?.name) parts.push(hike.value.mainCountry.name)
  return parts.length > 0 ? parts.join(', ') : i18n.global.t('completed_hikes.completed_hike_item.unknown_location')
})

const formattedDistance = computed(() => {
  const value = hike.value?.distanceMeters
  if (!value && value !== 0) return ''
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, true)
})

const formattedElevation = computed(() => {
  const value = hike.value?.elevationGainMeters
  if (!value && value !== 0) return ''
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, false)
})

const formattedDuration = computed(() => {
  const distance = hike.value?.distanceMeters
  const elevGain = hike.value?.elevationGainMeters
  if ((distance || distance === 0) && (elevGain || elevGain === 0)) {
    const secs = HikeTimeUtility.preference().secondsDuration(elevGain, distance)
    return new PrettyTime(secs).prettyTime()
  }
  return ''
})

// The badge shows the stored difficulty; known values are translated, anything
// else is shown as stored.
const difficultyLabel = computed(() => {
  const raw = hike.value?.difficulty
  switch ((raw || 'medium').toLowerCase()) {
    case 'easy': return i18n.global.t('shared.easy')
    case 'medium': return i18n.global.t('shared.medium')
    case 'difficult': return i18n.global.t('shared.difficult')
    case 'extra difficult': return i18n.global.t('shared.extra_difficult')
    default: return raw
  }
})

const difficultyClass = computed(() => {
  const difficulty = (hike.value?.difficulty || 'medium').toLowerCase()
  if (difficulty === 'easy') return 'difficulty-easy'
  if (difficulty === 'medium') return 'difficulty-medium'
  if (difficulty === 'difficult' || difficulty === 'hard') return 'difficulty-hard'
  if (difficulty === 'extra difficult') return 'difficulty-extra'
  return 'difficulty-medium'
})

function onShowFlyover() {
  emit('show-flyover', null, props.completedHike)
}

function onCopyShare() {
  emit('copy-share', null, props.completedHike)
}

function onAddToList() {
  emit('add-to-list', props.completedHike)
}

function onDownload() {
  emit('request-download', props.completedHike)
}
</script>

<style scoped>
/* Base Hike Item */
.completed-hike-item {
  background: #1f2026;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s;
}

.completed-hike-item:hover {
  background: #252530;
  border-color: rgba(255, 255, 255, 0.12);
}

.completed-hike-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  height: 100px;
}

/* Thumbnail Section */
.completed-hike-thumbnail-wrapper {
  position: relative;
  width: 160px;
  height: -webkit-fill-available;
  background: var(--gray);
  flex: 0 0 15%;
  overflow: hidden;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.completed-hike-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.completed-badge {
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
  background: rgba(40, 167, 69, 0.9);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.completed-badge i {
  font-size: 12px;
}

.play-button {
  position: absolute;
  top: .5em;
  left: .5em;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.play-button i {
  font-size: 20px;
}

/* Hike Info Section */
.completed-hike-info {
  flex: 0 0 23%;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.badges-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.difficulty-badge {
  display: inline-block;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
}

.difficulty-easy { background: #4BC263; color: #000; }
.difficulty-medium { background: #F57700; color: #000; }
.difficulty-hard { background: #DB2B35; color: #fff; }
.difficulty-extra { background: #8B008B; color: #fff; }

.private-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

.private-badge i {
  font-size: 12px;
}

.completed-hike-name {
  color: #fff;
  text-decoration: none;
  transition: text-decoration 0.2s;
}

.completed-hike-name:hover {
  color: #fff;
  text-decoration: underline;
}

.completed-date {
  color: rgba(255, 255, 255, 0.5);
  font-size: .75em;
}

/* Stats Section */
.completed-hike-stats {
  display: flex;
  flex: 0 0 32%;
  gap: 3rem;
  margin-top: 0.75rem;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-label {
  font-size: .5em;
  letter-spacing: 0.5px;
  color: #a6a6a6;
}

.stat p {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

/* Action Buttons */
.completed-hike-actions {
  display: flex;
  flex: 1;
  align-items: center;
  padding-right: 1em;
  gap: 0.75rem;
  margin-left: auto;
}

.btn-icon {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  width: 36px;
  height: 36px !important;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s;
  cursor: pointer;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.btn-icon i {
  font-size: 20px;
}

.btn-yellow {
  background: #FBCD01;
  color: #000;
  border: none;
}

.btn-yellow:hover {
  background: #e6ba00;
}

.btn-yellow i {
  color: #000;
}

/* Dropdowns */
.dropdown-toggle::after {
  display: none;
}

.dropdown-menu {
  background: #252530;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.dropdown-item {
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 14px;
}

.dropdown-item:hover,
.dropdown-item:focus-visible {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* Responsive */
@media (max-width: 992px) {
  .completed-hike-content {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .completed-hike-thumbnail-wrapper {
    width: 100%;
    height: 180px;
    flex: none;
  }

  .completed-hike-info {
    flex: none;
    padding: 0 .25rem;
  }

  .completed-hike-stats {
    flex: none;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 0;
  }

  .completed-hike-actions {
    flex: none;
    justify-content: flex-start;
    padding: 0 .25rem .5rem;
    margin-left: 0;
  }

  .btn-icon {
    width: 34px;
    height: 34px !important;
  }
}

@media (max-width: 576px) {
  .completed-hike-thumbnail-wrapper {
    height: 140px;
  }

  .completed-hike-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .btn-icon {
    width: 32px;
    height: 32px !important;
  }
}
</style>
